import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * StickyCTA — floating bottom "Start Free →" bar.
 *
 * Behaviour:
 * - Appears after the user scrolls past ~500px (so the hero CTA converts first)
 * - Hidden on editor / dashboard / admin / public invitation / auth routes so it
 *   doesn't fight the primary action on those pages.
 * - Mobile shows a compact pill; desktop shows the full promise + CTA.
 * - Dismissible via the × button; remembers the dismiss for the session.
 */

const GOLD = "#D4AF37";
const INK = "#0a0908";

const HIDDEN_PREFIXES = [
  "/editor",
  "/preview",
  "/dashboard",
  "/admin",
  "/i/",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/auth/callback",
  "/checkout",
  "/rsvps",
  "/guestbook",
  "/qr",
];

export default function StickyCTA() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("sticky_cta_dismissed") === "1") setDismissed(true);
    } catch (_) {}
  }, []);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hidden =
    dismissed ||
    HIDDEN_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/") || location.pathname.startsWith(p));

  if (hidden || !visible) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("sticky_cta_dismissed", "1"); } catch (_) {}
  };

  return (
    <div
      data-testid="sticky-cta-bar"
      className="fixed left-0 right-0 bottom-0 z-50 px-3 pb-3 md:pb-4 pointer-events-none"
      style={{ animation: "sticky-cta-slide-in 420ms cubic-bezier(0.2, 0.9, 0.3, 1.2)" }}
    >
      <style>{`
        @keyframes sticky-cta-slide-in {
          0% { transform: translateY(100%); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <div
        className="pointer-events-auto max-w-5xl mx-auto flex items-center justify-between gap-3 pl-4 md:pl-6 pr-2 py-2.5 md:py-3"
        style={{
          background: INK,
          border: `1px solid ${GOLD}`,
          borderRadius: "999px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.1)",
          backdropFilter: "blur(14px)",
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="hidden sm:inline-flex items-center justify-center flex-shrink-0"
            style={{ width: 32, height: 32, borderRadius: "999px", background: `${GOLD}22`, color: GOLD, fontSize: 14 }}
            aria-hidden
          >
            ★
          </span>
          <div className="min-w-0 leading-tight">
            <div className="font-serif-display text-base md:text-xl truncate" style={{ color: "#F2EBD8" }}>
              Your wedding website — <em style={{ color: GOLD, fontStyle: "normal", fontWeight: 500 }}>FREE</em> to build.
            </div>
            <div className="hidden md:block text-[11px] uppercase tracking-[0.3em] mt-0.5" style={{ color: "#a8a098" }}>
              No card · 23 templates · Pay only when ready
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Link
            to="/signup"
            data-testid="sticky-cta-signup"
            className="inline-flex items-center gap-2 px-4 md:px-7 py-2.5 md:py-3 text-[11px] md:text-xs uppercase tracking-[0.25em] font-semibold rounded-full transition-transform hover:scale-[1.03] whitespace-nowrap"
            style={{ background: GOLD, color: INK }}
          >
            Start Free <span aria-hidden>→</span>
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="sticky-cta-dismiss"
            className="flex items-center justify-center transition-opacity hover:opacity-100 opacity-60"
            style={{ width: 32, height: 32, color: "#a8a098", fontSize: 16, lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
